import {
  ATTENDANCE_STATUS_LABEL_PT_BR,
  CAPA_ACTION_STATUS_LABEL_PT_BR,
  CIRCULAR_STATUS_LABEL_PT_BR,
  NONCONFORMITY_STATUS_LABEL_PT_BR,
  type AttendanceStatus,
  type CapaActionStatus,
  type CircularStatus,
  type NonconformityStatus,
} from '@qualicloud/contracts';
import type {
  CapaDueState,
  CriticalityLevel,
  MeetingStatus,
} from '@qualicloud/domain';
import {
  Badge,
  IconAlertOctagon,
  IconAlertTriangle,
  IconCheckCircle,
  IconMinusCircle,
  cn,
} from '@/design-system';
import {
  ATTENDANCE_STATUS_TONE,
  CAPA_ACTION_STATUS_TONE,
  CIRCULAR_STATUS_TONE,
  MEETING_STATUS_LABEL,
  MEETING_STATUS_TONE,
  NONCONFORMITY_STATUS_TONE,
  presentCapaDue,
  presentCriticality,
} from '@/lib/sgq/presentation';

/**
 * Marcadores visuais do SGQ.
 *
 * REGRA ÚNICA DESTE ARQUIVO: cor nunca sozinha. Todo indicador aqui carrega
 * ícone de FORMA distinta + TEXTO, e o texto completo para leitor de tela vai
 * num `qc-sr-only` quando a versão visual precisa ser abreviada (célula de
 * tabela). É a mesma decisão de `<ValiditySemaphore>` e pelo mesmo motivo: o
 * legado transmitia criticidade e prazo apenas pelo fundo da célula, o que some
 * na impressão em preto e branco — que é o cenário de auditoria.
 */

const CRITICALITY_ICONS = {
  high: IconAlertOctagon,
  medium: IconAlertTriangle,
  low: IconCheckCircle,
} as const;

const DUE_ICONS = {
  overdue: IconAlertOctagon,
  'due-soon': IconAlertTriangle,
  'on-time': IconCheckCircle,
  done: IconMinusCircle,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Criticidade GUT
// ─────────────────────────────────────────────────────────────────────────────

export interface CriticalityBadgeProps {
  readonly score: number;
  readonly level: CriticalityLevel;
  /** `cell` na tabela; `chip` compacto; `block` no cabeçalho do detalhe. */
  readonly variant?: 'cell' | 'chip' | 'block';
  /** Distingue a GUT do EMISSOR da GUT do GESTOR, que são colunas separadas. */
  readonly origin?: 'reporter' | 'manager';
  readonly className?: string;
}

const ORIGIN_LABEL = {
  reporter: 'avaliação do emissor',
  manager: 'reavaliação do gestor',
} as const;

/**
 * Criticidade GUT: SCORE + FAIXA, com cor, ícone e texto.
 *
 * O score aparece sempre. Sem ele a faixa não diz o quanto uma NC "Alta" é
 * mais grave que outra "Alta" — e é o score que ordena a fila de CAPA.
 */
export function CriticalityBadge({
  score,
  level,
  variant = 'cell',
  origin,
  className,
}: CriticalityBadgeProps) {
  const presentation = presentCriticality({ score, level });
  const Icon = CRITICALITY_ICONS[presentation.iconName];
  const srText = origin
    ? `${presentation.srText} Origem: ${ORIGIN_LABEL[origin]}.`
    : presentation.srText;

  if (variant === 'chip') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
          presentation.className,
          className,
        )}
      >
        <Icon size={13} aria-hidden="true" />
        <span aria-hidden="true" className="qc-tabular">
          {presentation.label} · {score}
        </span>
        <span className="qc-sr-only">{srText}</span>
      </span>
    );
  }

  if (variant === 'block') {
    return (
      <div className={cn('rounded-md px-3 py-2', presentation.className, className)}>
        <p className="flex items-center gap-1.5 text-sm font-semibold">
          <Icon size={16} aria-hidden="true" />
          <span aria-hidden="true">Criticidade {presentation.label.toLowerCase()}</span>
          <span className="qc-sr-only">{srText}</span>
        </p>
        <p aria-hidden="true" className="mt-0.5 text-xs qc-tabular">
          Pontuação {score} de 125
          {origin ? ` — ${ORIGIN_LABEL[origin]}` : ''}
        </p>
      </div>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex w-full items-center gap-1.5 rounded px-2 py-1 text-sm',
        presentation.className,
        className,
      )}
    >
      <Icon size={14} aria-hidden="true" />
      <span aria-hidden="true" className="qc-tabular">
        {score}
      </span>
      <span aria-hidden="true" className="text-xs">
        {presentation.label}
      </span>
      <span className="qc-sr-only">{srText}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Semáforo de prazo da ação
// ─────────────────────────────────────────────────────────────────────────────

export interface CapaDueBadgeProps {
  readonly state: CapaDueState;
  readonly status: CapaActionStatus;
  /** `AAAA-MM-DD` — o `when` do 5W2H, que é DATE puro. */
  readonly dueDate: string;
  readonly variant?: 'cell' | 'chip';
  readonly className?: string;
}

/**
 * Semáforo de vencimento da ação corretiva.
 *
 * O estado vem PRONTO do servidor (`dueState`), calculado por
 * `resolveCapaDueState`. A data é reexibida junto porque "Vencida" sem a data
 * não diz há quanto tempo — e o prazo é o dado que o responsável precisa.
 */
export function CapaDueBadge({
  state,
  status,
  dueDate,
  variant = 'cell',
  className,
}: CapaDueBadgeProps) {
  const presentation = presentCapaDue({
    state,
    status,
    dueDate: parseIsoDateUtc(dueDate),
  });
  const Icon = DUE_ICONS[presentation.iconName];
  const formatted = formatIsoAsBr(dueDate);

  if (variant === 'chip') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
          presentation.className,
          className,
        )}
      >
        <Icon size={13} aria-hidden="true" />
        <span aria-hidden="true">{presentation.label}</span>
        <span className="qc-sr-only">{presentation.srText}</span>
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex w-full items-center gap-1.5 rounded px-2 py-1 text-sm',
        presentation.className,
        className,
      )}
    >
      <Icon size={14} aria-hidden="true" />
      <span aria-hidden="true" className="qc-tabular">
        {formatted}
      </span>
      <span className="qc-sr-only">{presentation.srText}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Situações
// ─────────────────────────────────────────────────────────────────────────────

export function NonconformityStatusBadge({
  status,
  className,
}: {
  readonly status: NonconformityStatus;
  readonly className?: string;
}) {
  return (
    <Badge tone={NONCONFORMITY_STATUS_TONE[status]} srPrefix="Situação:" className={className}>
      {NONCONFORMITY_STATUS_LABEL_PT_BR[status]}
    </Badge>
  );
}

export function MeetingStatusBadge({
  status,
  className,
}: {
  readonly status: MeetingStatus;
  readonly className?: string;
}) {
  return (
    <Badge tone={MEETING_STATUS_TONE[status]} srPrefix="Situação da reunião:" className={className}>
      {MEETING_STATUS_LABEL[status]}
    </Badge>
  );
}

export function CapaActionStatusBadge({
  status,
  className,
}: {
  readonly status: CapaActionStatus;
  readonly className?: string;
}) {
  return (
    <Badge tone={CAPA_ACTION_STATUS_TONE[status]} srPrefix="Situação da ação:" className={className}>
      {CAPA_ACTION_STATUS_LABEL_PT_BR[status]}
    </Badge>
  );
}

export function AttendanceStatusBadge({
  status,
  className,
}: {
  readonly status: AttendanceStatus;
  readonly className?: string;
}) {
  return (
    <Badge tone={ATTENDANCE_STATUS_TONE[status]} srPrefix="Presença:" className={className}>
      {ATTENDANCE_STATUS_LABEL_PT_BR[status]}
    </Badge>
  );
}

export function CircularStatusBadge({
  status,
  className,
}: {
  readonly status: CircularStatus;
  readonly className?: string;
}) {
  return (
    <Badge tone={CIRCULAR_STATUS_TONE[status]} srPrefix="Situação:" className={className}>
      {CIRCULAR_STATUS_LABEL_PT_BR[status]}
    </Badge>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Auxiliares
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `AAAA-MM-DD` → `Date` em UTC.
 *
 * `new Date('2026-03-14')` já é meia-noite UTC, mas depender disso é frágil:
 * uma string com hora (`2026-03-14T00:00:00-03:00`) mudaria o dia. O parse
 * textual é explícito e casa com `resolveCapaDueState`, que compara componentes
 * UTC.
 */
function parseIsoDateUtc(isoDate: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return new Date(Number.NaN);
  const [, year, month, day] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}

function formatIsoAsBr(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return '—';
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}
